import { isValid } from 'date-fns';
import moment from "moment";
import Multiselect from "multiselect-react-dropdown";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
	Button,
	Form
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import { withTranslation } from "react-i18next";
import { MdDragIndicator } from "react-icons/md";
import { FaCheck, FaChevronDown } from "react-icons/fa";
import { FiAlertCircle, FiChevronLeft, FiChevronRight, FiCornerDownRight, FiEdit2, FiPlus, FiSearch, FiTrash2, FiUploadCloud, FiUserMinus, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import { LoaderContext, RegulationContext } from "../../../../App";
import PaginationComponent from "../../../../components/Pagination/Pagination";
import ReportingPeriod from "../../../../components/Regulation/ReportingPeriod";
import MonthlyPeriodSelector from '../../../../components/ReportingPeriod/MonthlyPeriodSelector';
import { DEFAULT_ITEMS_PER_PAGE, FormFrequency, LIST_DEF_SORT, LS_AUTHORITY_KEY, SORT_ORDER_DESC, localeMap } from "../../../../core/constants/common";
import CalendarEventService from '../../../../core/service/CalendarEventService';
import FormService from "../../../../core/service/FormService";
import SupplierService from "../../../../core/service/SupplierService";
import Utils from "../../../../core/utilities/Utils";
import { countQuestionsInSkeleton } from "../../../../core/utilities/dceQuestionPermissionRows";
import GRI_pic from "../../../../images/GRI.jpg";
import "../Forms.scss";
import ConfirmationModal from "../../../../components/ConfirmationModal/ConfirmationModal";
import DeleteFormSectionModal from "../NewAssessmentModal/DeleteFormSectionModal";
import FormSectionModal from "../NewAssessmentModal/FormSectionModal";
import MoveQuestionModal from "../NewAssessmentModal/MoveQuestionModal";
import { applyQuestionMove, buildQuestionMove, findSkeletonSection } from "../NewAssessmentModal/questionMoveUtils";
import { applySectionMove, buildSectionMove, SECTION_MOVE_KIND } from "../NewAssessmentModal/sectionMoveUtils";
import { DCE_MODE } from "./dceMode";
import useDragAutoScroll from "./hooks/useDragAutoScroll";

const CardType = {
	FORM_SELECTION: 1,
	FORM_DETAILS: 2,
	PERMISSION: 3,
	PERMISSION_CONFIGURATION: 4,
	CONFIRMATION: 5
};

const SECTION_PUBLISH_STATE = {
	PENDING: 'PENDING',
	PUBLISHED: 'PUBLISHED'
};

// How long the "Published" badge stays in the action bar after a successful publish
const PUBLISHED_STATUS_VISIBLE_MS = 10 * 1000;

/** Data-owner emails from all branches (order preserved, duplicates allowed as returned by API). */
function getDataOwnerEmailsForGroup(group) {
	const emails = [];
	group?.branches?.forEach((b) => {
		b.emails?.forEach((e) => {
			if (e) emails.push(e);
		});
	});
	return emails;
}

/** Reviewers aggregated by approval level across branches. */
function getReviewersGroupedByLevel(group) {
	const levelMap = new Map();
	group?.branches?.forEach((b) => {
		b.reviewers?.forEach((r) => {
			const lvl = r.level ?? 1;
			if (!levelMap.has(lvl)) levelMap.set(lvl, new Set());
			const ident = (r.userEmail || r.userName || '').trim();
			if (ident) levelMap.get(lvl).add(ident);
		});
	});
	return [...levelMap.entries()]
		.sort((a, b) => a[0] - b[0])
		.map(([level, set]) => ({ level, emails: [...set] }));
}

// Create / update / duplicate data collection event page (four-step wizard).
// mode is a DCE_MODE value; without it an id means update, no id means create.
// In DUPLICATE mode, id is the source event: it is only read to pre-fill the form.
// enablePermCard opens an update straight on the permissions step.
function DataCollectionEventForm({ t, selfAssessment, mode, onHide, onSave, enablePermCard, id }) {
	const dceMode = mode ?? (id > 0 ? DCE_MODE.UPDATE : DCE_MODE.CREATE);
	const isUpdate = dceMode === DCE_MODE.UPDATE && id > 0;
	const isDuplicate = dceMode === DCE_MODE.DUPLICATE && id > 0;
	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyId = lsData.profile.company.id;
	const currentYear = new Date().getFullYear();
	const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0');

	const { glbRegulation } = useContext(RegulationContext);
	const { setLoaderDisplay } = useContext(LoaderContext);
	const permissionCard = selfAssessment ? CardType.PERMISSION_CONFIGURATION : CardType.PERMISSION;
	const [activeCard, setActiveCard] = useState(
		(enablePermCard) ? permissionCard : (id > 0 ? CardType.FORM_DETAILS : CardType.FORM_SELECTION)
	);

	const [itemsTotalCount, setItemTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE - 2);
	const [currentPage, setCurrentPage] = useState(1);
	const [forms, setForms] = useState([]);

	const [dataToSave, setDataToSave] = useState({
		period: `${currentYear}-${currentMonth}-01`,
		lastSubmissionDate: null,
		supplierIds: [],
		formPermissions: [],
		assessmentForm: null,
		regulation: null,
		frequency: FormFrequency.MONTHLY.text
	});
	const [suppliers, setSuppliers] = useState([]);
	const [formSkeleton, setFormSkeleton] = useState(null);
	const [groups, setGroups] = useState([]);
	const [detailsValidated, setDetailsValidated] = useState(false);
	const [permsValidated, setPermsValidated] = useState(false);
	const [selectedSection, setSelectedSection] = useState(null);
	const [sectionVisibility, setSectionVisibility] = useState({});
	const [sectionSearch, setSectionSearch] = useState('');
	const [recurringConfig, setRecurringConfig] = useState({
		recurringDay: 1,
		frequency: FormFrequency.MONTHLY.text,
		active: false,
		expiringDay: 1,
		startDate: `${currentYear}-${currentMonth}-01`,
		endDate: moment(new Date()).endOf('month').format('YYYY-MM-DD')
	});
	const [periodEditable, setPeriodEditable] = useState(false);

	const glbRegulationId = glbRegulation?.id ? glbRegulation.id : "";

	useEffect(() => {
		if (selfAssessment) return;

		if (!id) {
			getForms(0, itemsPerPage);
		}
		getSuppliers();
	}, [glbRegulationId]);

	useEffect(() => {
		if (!id) return;

		getAssessment();
	}, [id]);

	useEffect(() => {
		getForms(0, itemsPerPage, dataToSave.frequency);
	}, [dataToSave.frequency, glbRegulationId]);

	const getForms = (pageNumber, itemsPerPageVal, frequency = 'MONTHLY') => {
		if (pageNumber > 0) {
			pageNumber = pageNumber - 1;
		} else {
			setCurrentPage(1);
		}
		const perPage = itemsPerPageVal > 0 ? itemsPerPageVal : itemsPerPage;
		const sort = `${LIST_DEF_SORT}, ${SORT_ORDER_DESC}`;
		FormService.getForms(
			pageNumber,
			perPage,
			'',
			sort,
			glbRegulation?.id,
			selfAssessment,
			true,
			true,
			frequency,
			'GENERAL'
		).then((response) => {
			setItemTotalCount(parseFloat(response.headers["x-total-count"]));
			setForms([...response.data]);
		}).catch((error) => { });
	};

	const getSuppliers = () => {
		SupplierService.listAllSuppliers(companyId)
			.then((response) => {
				// Filter suppliers where companyId is not null
				const filteredSuppliers = response.data.filter(supplier => supplier.companyId !== null);
				// Create the "Select All" option for selectAll implementation in multidropdown
				const selectAllOption = { companyId: 'selectAll', legalName: 'Select All' };
				setSuppliers([selectAllOption, ...filteredSuppliers]);
			})
			.catch((error) => {
				console.error("Error fetching suppliers:", error);
			});
	};

	const getFormPreview = (id) => {
		if (!id) return;

		FormService.getFormPreview(id).then(res => {
			if (res.status !== 200) return;

			setFormSkeleton(res.data);
		}).catch(err => { });
	};

	const getGroups = () => {
		FormService.listGroups().then(res => {
			if (res.status !== 200) return;

			setGroups(res.data);
		}).catch(err => { });
	};

	const getAssessment = () => {

		setLoaderDisplay(true);
		FormService.getAssessment(id).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) {
				// Nothing to edit or copy; the interceptor has already shown the error
				exitPage();
				return;
			}

			// Fetch all sections for the forms and group
			const assessmentForm = res.data.assessmentForm;
			getFormPreview(assessmentForm.id);
			getGroups();
			// Populate form data
			const suppliers = [];
			const perms = [];
			res.data.sharedWith?.forEach(s => suppliers.push(s.id));

			res.data.formPermissions?.forEach(p => {
				const perm = {
					groups: [],
					section: p.section
						? { id: p.section.id, title: p.section.title }
						: { id: -1, title: t('CompleteForm.label') }
				};
				p.groups?.forEach(g => perm.groups.push({ id: g.id }));
				perms.push(perm);
			});
			setDataToSave({
				period: res.data.period,
				// A duplicate needs a future expiry; a past one is cleared so validation asks for a new date
				lastSubmissionDate: isDuplicate && moment(res.data.lastSubmissionDate).isBefore(moment(), 'day')
					? null
					: res.data.lastSubmissionDate,
				supplierIds: suppliers,
				formPermissions: perms,
				assessmentForm: {
					id: assessmentForm.id,
					name: assessmentForm.name,
					description: assessmentForm.description
				},
				regulation: assessmentForm.regulation,
				frequency: res.data.frequency,
				// A duplicate gets its own calendar event on save; keeping the source's ref would
				// let the Once/Recurring switch activate or deactivate the original's schedule
				calendarEventRef: isDuplicate ? null : res.data.calendarEventRef,
				remindBefore: res.data.remindBefore,
				reminderEnabled: res.data.reminderEnabled,
				daysOffset: res.data.daysOffset
			});
			setActiveCard(
				(enablePermCard) ? permissionCard : CardType.FORM_DETAILS
			);
			//  If the assessment is in not started state then reporting period can be edited
			setPeriodEditable(isDuplicate || (!res.data.sharedOn && (new Date(res.data.lastSubmissionDate) > new Date())));
			// Read only: the source's recurring settings pre-fill the duplicate's schedule
			getCalendarEvent(res.data.calendarEventRef, res.data);
		}).catch(err => {
			setLoaderDisplay(false);
			exitPage();
		});

	};

	const exitPage = () => {
		onHide?.();
	};
	const onFormSelection = (form, regulation, advance = true) => {
		const isNewForm = form.id !== dataToSave.assessmentForm?.id;
		if (isNewForm) {
			setSelectedSection(null);
			setSectionSearch('');
			// Section edits and their publish state belong to the previously selected form
			setCreatedSectionIds([]);
			setSectionPublishState(null);
			updatePendingQuestionMoves([]);
		}

		setDataToSave({
			...dataToSave,
			// Section permissions belong to the previously selected form's skeleton
			formPermissions: isNewForm ? [] : dataToSave.formPermissions,
			assessmentForm: {
				id: form.id,
				name: form.name,
				description: form.description
			},
			regulation: regulation
		});
		if (advance) setActiveCard(CardType.FORM_DETAILS);

		if (!selfAssessment || !isNewForm) return;

		getFormPreview(form.id);
		getGroups();
	};

	const onBack = () => {
		const prevCard = activeCard - 1;
		if (selfAssessment) {
			if (activeCard === CardType.PERMISSION_CONFIGURATION) {
				setActiveCard(CardType.FORM_DETAILS);
			} else {
				setActiveCard(prevCard ?? CardType.FORM_SELECTION);
			}
		} else {
			if (activeCard === CardType.CONFIRMATION) {
				setActiveCard(CardType.PERMISSION);
			} else {
				setActiveCard(prevCard ?? CardType.FORM_SELECTION);
			}
		}
	};
	const onNext = () => {
		if (activeCard === CardType.FORM_DETAILS
			&& (!dataToSave.lastSubmissionDate || !dataToSave.period)) {
			setDetailsValidated(true);
			return
		}
		if (activeCard === CardType.PERMISSION
			&& dataToSave.supplierIds?.length === 0) {
			setPermsValidated(true);
			return;
		}
		if (activeCard === CardType.PERMISSION_CONFIGURATION
			&& dataToSave.formPermissions?.length === 0) {
			setPermsValidated(true);
			return;
		}
		setPermsValidated(false);
		const nextCard = activeCard + 1;
		if (selfAssessment) {
			if (activeCard === CardType.FORM_DETAILS) {
				setActiveCard(CardType.PERMISSION_CONFIGURATION);
			} else {
				setActiveCard(nextCard > CardType.CONFIRMATION ? CardType.CONFIRMATION : nextCard);
			}
		} else {
			if (activeCard === CardType.PERMISSION) {
				setActiveCard(CardType.CONFIRMATION);
			} else {
				setActiveCard(nextCard > CardType.CONFIRMATION ? CardType.CONFIRMATION : nextCard);
			}
		}
	};

	const onSelectDeSelectSupplier = (selectedItem) => {
		const supplierIds = [...dataToSave.supplierIds];

		const index = dataToSave.supplierIds.indexOf(selectedItem.companyId);
		if (index >= 0) {
			supplierIds.splice(index, 1);
		} else {
			supplierIds.push(selectedItem.companyId);
		}
		setDataToSave({
			...dataToSave,
			supplierIds: supplierIds
		});
	};

	const setLastSubmissionDate = (date) => {
		setDataToSave({
			...dataToSave,
			lastSubmissionDate: moment(date).format('YYYY-MM-DD')
		});
	};

	const saveAssessment = () => {
		if (isUpdate) {
			createOrUpdatAssessment();
			return;
		}
		setLoaderDisplay(true);
		// When creating a new assessment, check if there is an existing assessment with the same reporting period and form
		FormService.searchAssessment(dataToSave.assessmentForm.id, dataToSave.period)
			.then(ares => {
				setLoaderDisplay(false);
				if (ares.status !== 200) return;

				if (ares.data?.id > 0) {
					// If yes do not create a new assessment
					toast.error(t("AssessmentExists.label"), {
						position: toast.POSITION.TOP_RIGHT,
						autoClose: true
					});
					return;
				}
				// Otherwise proceed to assessment creation.
				createOrUpdatAssessment();
			}).catch(err => { setLoaderDisplay(false); });
	};

	const createOrUpdatAssessment = () => {
		if (selfAssessment) {
			delete dataToSave.supplierIds;
		} else {
			delete dataToSave.formPermissions;
		}
		delete dataToSave.regulation;
		delete dataToSave.assessmentForm.name;
		delete dataToSave.assessmentForm.description;
		let req;
		let payload = dataToSave;
		if (recurringConfig.active) {
			payload = {
				...payload,
				recurringConfigActive: recurringConfig.active,
				calendarEvent: {
					recurringDay: recurringConfig.recurringDay,
					frequency: recurringConfig.frequency,
					active: recurringConfig.active,
					expiringDay: recurringConfig.expiringDay,
					startDate: recurringConfig.startDate,
					endDate: recurringConfig.endDate
				}
			};
		}
		// Only UPDATE writes to the existing event; CREATE and DUPLICATE always create a new one
		if (isUpdate) {
			req = FormService.updateAssessment(id, payload);
		} else {
			req = FormService.createAssessment(payload);
		}

		const isNewAssessment = !isUpdate;

		setLoaderDisplay(true);
		req.then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			const successMessage = isUpdate ? "Success" : t('AssessmentCreationSuccessMessage.label');
			toast.success(successMessage, {
				position: toast.POSITION.TOP_RIGHT,
			});

			// Newly created assessments get a default reminder (3 days before expiry) enabled automatically.
			if (isNewAssessment && res.data?.id > 0) {
				FormService.updateAssessment(res.data.id, {
					...res.data,
					remindBefore: 3,
					reminderEnabled: true,
					daysOffset: 1
				}).catch(err => { }).finally(() => {
					exitPage();
					onSave?.();
				});
				return;
			}

			exitPage();
			onSave?.();
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};


	const onGroupCheck = (id) => {
		const perms = JSON.parse(JSON.stringify(dataToSave.formPermissions));
		const permIndex = perms.findIndex(p => p.section?.id === selectedSection?.id);
		if (permIndex >= 0) {
			const existingGroups = perms[permIndex].groups;
			const groupIndex = existingGroups?.findIndex(g => g.id === id);
			if (groupIndex >= 0) {
				perms[permIndex].groups.splice(groupIndex, 1);
				if (perms[permIndex].groups?.length == 0) {
					perms.splice(permIndex, 1);
				}
			} else {
				perms[permIndex].groups.push({ id: id });
			}
		} else {
			perms.push({
				section: {
					id: selectedSection?.id,
					title: selectedSection?.title,
				},
				groups: [{ id: id }]
			});
		}
		setDataToSave({
			...dataToSave,
			formPermissions: perms
		});
	};

	const getSelectedSuppliers = () => {
		return suppliers?.filter(s => dataToSave.supplierIds?.indexOf(s.companyId) >= 0);
	};

	const isPermittedForGroup = (groupId) => {
		const index = dataToSave.formPermissions?.findIndex(p => p.section?.id === selectedSection.id);
		if (index < 0) return false;

		return dataToSave.formPermissions[index].groups?.findIndex(g => g.id === groupId) >= 0;
	}

	const onPermissionDelete = (sectionId) => {
		const perms = JSON.parse(JSON.stringify(dataToSave.formPermissions));
		const permIndex = perms.findIndex(p => p.section?.id === sectionId);
		if (permIndex < 0 || !perms[permIndex]) return;

		perms.splice(permIndex, 1);
		setDataToSave({
			...dataToSave,
			formPermissions: perms
		});
	};

	const hasPermissionForSection = (sectionId) => {
		const index = dataToSave?.formPermissions?.findIndex(p => p.section.id === sectionId);
		if (index < 0) return false;

		return dataToSave?.formPermissions[index]?.groups?.length > 0;
	};

	const [groupSearch, setGroupSearch] = useState('');

	const PERMISSION_GROUP_EMAIL_LIMIT = 3;

	// Up to the limit as chips, the rest folded into a "+N more" chip whose tooltip lists them
	const renderPermissionGroupEmails = (keyPrefix, emails) => {
		const hidden = emails.slice(PERMISSION_GROUP_EMAIL_LIMIT);
		return (
			<div className="perm-groups__emails">
				{emails.slice(0, PERMISSION_GROUP_EMAIL_LIMIT).map((email, index) => (
					<span key={`${keyPrefix}-${index}`} className="perm-groups__email" title={email}>{email}</span>
				))}
				{hidden.length > 0 && (
					<span className="perm-groups__email perm-groups__email--more" title={hidden.join('\n')}>
						{t('MoreSelected.label', { count: hidden.length })}
					</span>
				)}
			</div>
		);
	};

	const renderPermissionGroupDetails = (group) => {
		const ownerEmails = getDataOwnerEmailsForGroup(group);
		const approversByLevel = getReviewersGroupedByLevel(group);
		return (
			<div className="perm-groups__details">
				<div className="perm-groups__detail">
					<span className="perm-groups__detail-label">{t('DataOwners.label')}</span>
					{ownerEmails.length > 0
						? renderPermissionGroupEmails(`owner-${group.id}`, ownerEmails)
						: <span className="perm-groups__empty">—</span>}
				</div>
				<div className="perm-groups__detail">
					<span className="perm-groups__detail-label">{t('Approvers.label')}</span>
					{approversByLevel.length > 0
						? approversByLevel.map(({ level, emails }) => (
							<div key={`level-${group.id}-${level}`} className="perm-groups__level"
								title={t('AssessmentFormModal.approverLevel.label', { level })}>
								<span className="perm-groups__level-badge">{t('LevelShort.label', { level })}</span>
								{renderPermissionGroupEmails(`approver-${group.id}-${level}`, emails)}
							</div>
						))
						: <span className="perm-groups__empty">—</span>}
				</div>
			</div>
		);
	};

	const renderPermissionGroupCard = (group) => {
		const isChecked = isPermittedForGroup(group.id);
		const inputId = `group-input-${group.id}`;
		return (
			<li key={`group-${group.id}`} className={`perm-groups__card ${isChecked ? 'is-checked' : ''}`}>
				<label className="perm-groups__card-head" htmlFor={inputId}>
					<input className="form-check-input perm-groups__check" type="checkbox"
						id={inputId}
						checked={isChecked}
						onChange={() => onGroupCheck(group.id)} />
					<span className="perm-groups__avatar" aria-hidden>{Utils.getTextBasedProfilePic(group.groupName)}</span>
					<span className="perm-groups__name" title={group.groupName}>{group.groupName}</span>
				</label>
				{renderPermissionGroupDetails(group)}
			</li>
		);
	};

	const renderPermissionGroups = () => {
		const query = groupSearch.trim().toLowerCase();
		const visibleGroups = (groups ?? []).filter(group => !query || (group.groupName ?? '').toLowerCase().includes(query));
		const selectedCount = (groups ?? []).filter(group => isPermittedForGroup(group.id)).length;
		return (
			<div className="AllSection_Viewcard card perm-groups">
				<div className="perm-groups__toolbar">
					<div className="perm-groups__toolbar-row">
						<span className="perm-groups__title">{t('Groups.label')}</span>
						<span className="perm-groups__count">
							{t('CountOfTotalSelected.label', { count: selectedCount, total: groups?.length ?? 0 })}
						</span>
					</div>
					<div className="perm-groups__search">
						<FiSearch className="perm-groups__search-icon" aria-hidden />
						<input type="search" className="form-control form-control-sm"
							value={groupSearch}
							placeholder={t('SearchGroups.label')}
							aria-label={t('SearchGroups.label')}
							onChange={(e) => setGroupSearch(e.target.value)} />
					</div>
				</div>
				{visibleGroups.length > 0 ? (
					<ul className="perm-groups__list">
						{visibleGroups.map(group => renderPermissionGroupCard(group))}
					</ul>
				) : query && (
					<div className="perm-groups__no-match">
						<FiSearch aria-hidden />
						<span>{t('NoGroupsMatchQuery.label', { query: groupSearch.trim() })}</span>
					</div>
				)}
			</div>
		);
	};

	const renderRecurringConfig = () => {
		if (!recurringConfig.active) return <></>;

		return (
			<>
				{
					id > 0 && !periodEditable &&
					<div className="p-2">
						<label className='mb-1'>{t('Period.label')}</label>
						<ReportingPeriod
							disabled={true}
							defaultPeriod={id > 0 ? dataToSave.period : null}
							defaultFrequency={'MONTHLY'}
							valueChangeCallback={(period) => null} />
					</div>
				}
				<div className="p-2">
					<div className='DataCollection_period_form'>
						<div className='DataCollection_period'>
							<label className='mb-1'>{t('From.label')}</label>
							<MonthlyPeriodSelector defaultPeriod={recurringConfig.startDate}
								valueChangeCallback={(period) => {
									if (id > 0 && !periodEditable) {
										setRecurringConfig({ ...recurringConfig, startDate: `${period.year}-${period.month}-01` });
									} else {
										setDataToSave({ ...dataToSave, period: `${period.year}-${period.month}-01` });
										setRecurringConfig({ ...recurringConfig, startDate: `${period.year}-${period.month}-01` });
									}
								}} />
						</div>
						<div className='DataCollection_period'>
							<label className='mb-1'>{t('To.label')}</label>
							<MonthlyPeriodSelector defaultPeriod={recurringConfig.endDate}
								minPeriod={recurringConfig.startDate}
								valueChangeCallback={(period) => {
									const d = moment(`${period.year}-${period.month}-01`).endOf('month').format('YYYY-MM-DD');
									setRecurringConfig({ ...recurringConfig, endDate: d });
								}} />
						</div>
					</div>
				</div>
				<div className='p-2 mb-3'>
					<div className="pb-3">
						<strong>{Utils.getRecurrenceText(recurringConfig.frequency)}</strong>
					</div>
					<div className='RepeatEveryDay_card'>
						<div className='RepeatEveryDayBox'>
							<label>Starts on day </label>
							<select className="form-select"
								value={recurringConfig.recurringDay}
								onChange={(e) => {
									setRecurringConfig({ ...recurringConfig, recurringDay: +e.target.value });
									setDataToSave({
										...dataToSave,
										lastSubmissionDate: moment(dataToSave.period)
											.add(+e.target.value, 'days')
											.add(recurringConfig.expiringDay, 'days')
											.subtract(1, 'days')
											.format('YYYY-MM-DD')
									});
								}}>
								{[...Array(31)].map((_, index) => {
									const day = index + 1;
									return (
										<option value={day} key={day}>
											{day}
										</option>
									);
								})}
							</select>
						</div>
						<div className='RepeatEveryDayBox'>
							<label>Valid for </label>
							<input
								type="number"
								className="form-control ms-2 me-2"
								value={recurringConfig.expiringDay}
								min="1" max="31"
								onChange={(e) => onExpiryDayChange(+e.target.value)}
								onBlur={(e) => {
									if (!e.target.value || +e.target.value < 1 || +e.target.value > 60) {
										onExpiryDayChange(1);
										toast.warning("Validity must be between 1 and 60 days", {
											position: toast.POSITION.TOP_RIGHT,
										});
									}
								}}
							/>
							<label> day(s)</label>
						</div>
					</div>
				</div>
			</>
		);
	};

	const getCalendarEvent = (id, assessmentData) => {
		if (!id) return;

		CalendarEventService.getEvent(id).then(res => {
			if (res.status !== 200) return;

			setRecurringConfig({
				...recurringConfig,
				recurringDay: res.data?.recurringDay,
				frequency: res.data?.frequency,
				active: res.data?.active,
				expiringDay: res.data?.expiringDay ?? 1,
				startDate: res.data?.startDate ?? assessmentData.period,
				endDate: res.data?.endDate ?? assessmentData.lastSubmissionDate
			});
		}).catch(err => { });
	};

	const enableOrDisableCalendarEvent = (active) => {
		setRecurringConfig({ ...recurringConfig, active: active });
		if (active && (!id || isDuplicate)) {
			// Only set expiry date for a new (or duplicated) DCE while enabling recurring
			setDataToSave({
				...dataToSave,
				lastSubmissionDate: moment(dataToSave.period)
					.add(recurringConfig.recurringDay, 'days')
					.add(recurringConfig.expiringDay, 'days')
					.subtract(1, 'days')
					.format('YYYY-MM-DD')
			});
		}
		if (!dataToSave.calendarEventRef) return;

		CalendarEventService.activateEvent(dataToSave.calendarEventRef, companyId, active).then(res => {
			if (res.status !== 200) return;

			setRecurringConfig({
				...recurringConfig,
				active: active,
			});
		}).catch(err => { });
	};

	const onExpiryDayChange = (val) => {
		setRecurringConfig({ ...recurringConfig, expiringDay: val });
		setDataToSave({
			...dataToSave,
			lastSubmissionDate: moment(dataToSave.period)
				.add(recurringConfig.recurringDay, 'days')
				.add(val, 'days')
				.subtract(1, 'days')
				.format('YYYY-MM-DD')
		});
	};

	const onFrequencyChange = (frequency) => {
		// Forms are listed per frequency, so a previously selected form no longer applies
		setDataToSave({ ...dataToSave, frequency: frequency, assessmentForm: null, regulation: null, formPermissions: [] });
		setRecurringConfig({ ...recurringConfig, frequency: frequency });
		setSelectedSection(null);
	};

	const renderOnceFields = () => (
		<>
			<div className="row m-0 mb-2">
				<div className="col-md-12 p-2">
					<ReportingPeriod
						disabled={isUpdate && !periodEditable}
						defaultPeriod={id > 0 ? dataToSave.period : null}
						defaultFrequency={'MONTHLY'}
						valueChangeCallback={(period) =>
							setDataToSave({ ...dataToSave, period: `${period.year}-${period.month}-01` })
						} />
				</div>
			</div>
			<div className="row m-0 mb-3">
				<div className="col-md-12 p-2">
					<label className='mb-1'>{t('Expiry.label')}</label>
					<div className="pb-2">
						<DatePicker
							selected={
								dataToSave.lastSubmissionDate && isValid(new Date(dataToSave.lastSubmissionDate))
									? new Date(dataToSave.lastSubmissionDate)
									: null
							}
							onChange={(date) => setLastSubmissionDate(date)}
							className="form-control"
							locale={localeMap[t("datePicker.locale")]}
							required
							dateFormat="dd/MM/yyyy"
							minDate={new Date()}
						/>
					</div>
				</div>
			</div>
		</>
	);

	const PERMISSION_NODE_AVATAR_LIMIT = 4;

	const getAssignedGroupsForSection = (sectionId) =>
		dataToSave.formPermissions?.find(p => p?.section?.id === sectionId)?.groups ?? [];

	// A section/sub-section can expand when it has its own questions or nested sub-sections
	const isSectionExpandable = (section) => section?.subSections?.length > 0 || section?.fields?.length > 0;

	const collectExpandableSectionIds = (sections, ids = []) => {
		sections?.forEach(section => {
			if (isSectionExpandable(section)) ids.push(section.id);
			collectExpandableSectionIds(section?.subSections, ids);
		});
		return ids;
	};

	const toggleSectionExpanded = (sectionId) => {
		setSectionVisibility({ ...sectionVisibility, [sectionId]: !sectionVisibility[sectionId] });
	};

	const setAllSectionsExpanded = (expanded) => {
		const visibility = {};
		collectExpandableSectionIds(formSkeleton?.sections).forEach(sectionId => {
			visibility[sectionId] = expanded;
		});
		setSectionVisibility(visibility);
	};

	const selectSectionOnKey = (e, section) => {
		if (e.key !== 'Enter' && e.key !== ' ') return;
		e.preventDefault();
		setSelectedSection(section);
	};

	const renderPermissionNodeGroups = (sectionId) => {
		const assignedGroups = getAssignedGroupsForSection(sectionId);
		if (assignedGroups.length === 0) {
			return <span className="perm-node__no-groups">{t('NoGroupsAssigned.label')}</span>;
		}
		const hiddenCount = assignedGroups.length - PERMISSION_NODE_AVATAR_LIMIT;
		return (
			<div className="perm-node__avatars">
				{assignedGroups.slice(0, PERMISSION_NODE_AVATAR_LIMIT).map(group => {
					const groupName = groups?.find(g => g.id === group.id)?.groupName ?? '';
					return (
						<span className="perm-node__avatar" key={`permAvatar-${sectionId}-${group.id}`} title={groupName}>
							{Utils.getTextBasedProfilePic(groupName)}
						</span>
					);
				})}
				{hiddenCount > 0 && <span className="perm-node__avatar perm-node__avatar--more">+{hiddenCount}</span>}
			</div>
		);
	};

	// Icon-only button in a node's action bar; the label doubles as tooltip and accessible name
	const renderPermissionNodeAction = (type, icon, label, onClick) => (
		<button type="button" key={`permAction-${type}`}
			className={`perm-node__action perm-node__action--${type}`}
			aria-label={label} title={label}
			onClick={(e) => {
				e.stopPropagation();
				onClick();
			}}>
			{icon}
		</button>
	);

	const renderPermissionNode = ({ id: sectionId, title, badge, depth = 0, questionCount, subSections = [], childEntries = [], node, canAddSubsection = false }) => {
		const isSelected = selectedSection?.id === sectionId;
		const isAssigned = hasPermissionForSection(sectionId);
		const hasChildren = subSections.length > 0;
		const isExpandable = isSectionExpandable(node);
		const isExpanded = sectionVisibility[sectionId] === true;
		const select = () => setSelectedSection({ id: sectionId, title: title });
		// Only sections/sub-sections created on this page can be removed
		const canDeleteSection = !!node && canEditSections && createdSectionIds.includes(sectionId);
		const isQuestionDropSection = !!node && draggedQuestionId != null && questionDropTarget?.sectionId === sectionId;
		// Sub-sections can be dragged to another position or into another section
		const isSectionDraggable = !!node && depth > 0 && canEditSections;
		const sectionDragClass = `${isSectionDraggable ? 'is-section-draggable' : ''} ${draggedSectionId === sectionId ? 'is-dragging' : ''} ${getSectionDropClass(sectionId)}`;

		return (
			<li key={`permNode-${sectionId}`} className="perm-tree__item" role="treeitem"
				aria-selected={isSelected} aria-expanded={isExpandable ? isExpanded : undefined}>
				<div className={`perm-node ${isSelected ? 'is-selected' : ''} ${isAssigned ? 'is-assigned' : ''} ${isQuestionDropSection ? 'is-drop-target' : ''} ${sectionDragClass}`}
					draggable={isSectionDraggable}
					onDragStart={isSectionDraggable ? (e) => onSubSectionDragStart(e, sectionId) : undefined}
					onDragEnd={isSectionDraggable ? onSubSectionDragEnd : undefined}
					{...(node ? getSectionDropProps(sectionId, depth) : {})}>
					{isSectionDraggable && (
						<MdDragIndicator className="perm-node__grip" aria-label={t('DragToReorder.label')} title={t('DragToReorder.label')} />
					)}
					{isExpandable && (
						<button type="button" className="perm-node__toggle"
							aria-label={title} aria-expanded={isExpanded}
							onClick={() => toggleSectionExpanded(sectionId)}>
							<FaChevronDown className={isExpanded ? 'is-open' : ''} />
						</button>
					)}
					<div className="perm-node__main cursor" role="button" tabIndex={0} data-section-id={sectionId}
						onClick={select}
						onKeyDown={(e) => selectSectionOnKey(e, { id: sectionId, title: title })}>
						<div className="perm-node__title-row">
							<span className={`perm-node__badge perm-node__badge--${badge.type}`}>{badge.label}</span>
							<span className="perm-node__title" title={title}>{renderHighlightedTitle(title)}</span>
						</div>
						<div className="perm-node__meta">
							{hasChildren && <span>{subSections.length} {t('SubSections.label')}</span>}
							<span>{questionCount} {t('Questions.label')}</span>

						</div>
					</div>
					<div className="perm-node__side">
						<div className="perm-node__groups">
							{renderPermissionNodeGroups(sectionId)}
						</div>
						{(canAddSubsection || canDeleteSection || isAssigned) && (
							<div className="perm-node__actions" role="toolbar" aria-label={title}>
								{canAddSubsection && renderPermissionNodeAction(
									'add', <FiPlus />, t('AddSubSection.label'),
									() => openSectionCreateModal(true, sectionId)
								)}
								{isAssigned && renderPermissionNodeAction(
									'unassign', <FiUserMinus />, t('RemovePermission.label'),
									() => onPermissionDelete(sectionId)
								)}
								{canDeleteSection && renderPermissionNodeAction(
									'delete', <FiTrash2 />,
									t(depth === 0 ? 'RemoveSection.label' : 'RemoveSubSection.label'),
									() => openSectionDeleteModal({ id: sectionId, title: title }, depth > 0)
								)}
							</div>
						)}
					</div>
					{node && isExpanded && (
						// Full card width, below the title/groups row
						<div className="perm-node__questions cursor" onClick={select}>
							{renderSectionQuestions(node)}
						</div>
					)}
				</div>
				{childEntries.length > 0 && isExpanded && (
					<ul className="perm-tree__children" role="group">
						{childEntries.map(entry => renderSectionNode(entry, depth + 1))}
					</ul>
				)}
			</li>
		);
	};

	// entry = { section, children } from buildSectionEntries / filterSectionEntries
	const renderSectionNode = ({ section, children }, depth = 0) => renderPermissionNode({
		id: section.id,
		title: section.title,
		badge: depth === 0
			? { type: 'section', label: t('DCEQuestionPermissions.badge.section.label') }
			: { type: 'subsection', label: t('DCEQuestionPermissions.badge.subsection.label') },
		depth: depth,
		questionCount: countQuestionsInSkeleton({ sections: [section] }),
		subSections: section?.subSections ?? [],
		childEntries: children,
		node: section,
		// Sub-sections are created under top-level sections, same as the form builder's parent list
		canAddSubsection: depth === 0
	});

	const normalizedSectionSearch = sectionSearch.trim().toLowerCase();

	const sectionTitleMatches = (title) => (title ?? '').toLowerCase().includes(normalizedSectionSearch);

	const buildSectionEntries = (sections) =>
		(sections ?? []).map(section => ({ section, children: buildSectionEntries(section?.subSections) }));

	// A section's own questions (not its sub-sections') whose text matches the search
	const sectionHasQuestionMatch = (section, query = normalizedSectionSearch) =>
		(section?.fields ?? []).some(question => (question?.name ?? '').toLowerCase().includes(query));

	// Keeps sections/sub-sections whose title or questions match, plus their ancestors;
	// a title match keeps its full subtree, a question-only match keeps just the matching branches
	const filterSectionEntries = (sections) => (sections ?? []).reduce((entries, section) => {
		if (sectionTitleMatches(section.title)) {
			entries.push({ section, children: buildSectionEntries(section?.subSections) });
		} else {
			const children = filterSectionEntries(section?.subSections);
			if (children.length > 0 || sectionHasQuestionMatch(section)) entries.push({ section, children });
		}
		return entries;
	}, []);

	const onSectionSearchChange = (value) => {
		setSectionSearch(value);
		const query = value.trim().toLowerCase();
		if (!query) return;

		// Open every section that contains a match deeper down, or matching questions, so the match is visible
		const ids = [];
		const walk = (sections) => {
			let hasMatch = false;
			(sections ?? []).forEach(section => {
				const descendantMatch = walk(section?.subSections);
				const questionMatch = sectionHasQuestionMatch(section, query);
				if (descendantMatch || questionMatch) ids.push(section.id);
				if (descendantMatch || questionMatch || (section.title ?? '').toLowerCase().includes(query)) hasMatch = true;
			});
			return hasMatch;
		};
		walk(formSkeleton?.sections);
		if (ids.length === 0) return;

		const visibility = { ...sectionVisibility };
		ids.forEach(sectionId => { visibility[sectionId] = true; });
		setSectionVisibility(visibility);
	};

	const renderHighlightedTitle = (title) => {
		if (!normalizedSectionSearch || !title) return title;

		const start = title.toLowerCase().indexOf(normalizedSectionSearch);
		if (start < 0) return title;

		const end = start + normalizedSectionSearch.length;
		return (
			<>
				{title.slice(0, start)}
				<mark className="perm-node__match">{title.slice(start, end)}</mark>
				{title.slice(end)}
			</>
		);
	};

	const [sectionCreate, setSectionCreate] = useState({ show: false, isSubsection: false, parentId: null });

	const openSectionCreateModal = (isSubsection, parentId = null) => {
		setSectionCreate({ show: true, isSubsection: isSubsection, parentId: parentId });
	};

	const closeSectionCreateModal = () => setSectionCreate({ ...sectionCreate, show: false });

	// Reload the skeleton (bypassing the preview cache) so the new node shows up in the tree right away
	// Only sections/sub-sections created here can be removed; the form's existing ones stay untouched
	const [createdSectionIds, setCreatedSectionIds] = useState([]);

	// null = no section changes yet, PENDING = unpublished section changes, PUBLISHED = published on this page
	const [sectionPublishState, setSectionPublishState] = useState(null);
	const [showPublishConfirm, setShowPublishConfirm] = useState(false);

	// Question and sub-section moves are only shown locally until Publish, which saves them through the form
	// builder's field/section reorder APIs in the order they were made and then publishes the form
	const [, setPendingQuestionMoves] = useState([]);
	const pendingQuestionMovesRef = useRef([]);

	const updatePendingQuestionMoves = (moves) => {
		pendingQuestionMovesRef.current = moves;
		setPendingQuestionMoves(moves);
	};

	// A reloaded skeleton comes from the server, so re-apply the moves that are not saved yet
	const applyPendingMove = (skeleton, move) =>
		move.kind === SECTION_MOVE_KIND ? applySectionMove(skeleton, move) : applyQuestionMove(skeleton, move);

	// Moves that can no longer be applied (e.g. their section was removed) are dropped
	const applyFetchedSkeleton = (skeleton) => {
		const replayed = pendingQuestionMovesRef.current.reduce((result, move) => {
			const next = applyPendingMove(result.skeleton, move);
			return next ? { skeleton: next, moves: [...result.moves, move] } : result;
		}, { skeleton, moves: [] });
		setFormSkeleton(replayed.skeleton);
		updatePendingQuestionMoves(replayed.moves);
		return replayed.skeleton;
	};

	// Same publish API and confirmation as the form list; nothing is published automatically
	const onPublishSectionChanges = (confirmed) => {
		setShowPublishConfirm(false);
		if (!confirmed) return;

		const formId = dataToSave.assessmentForm?.id;
		const moves = pendingQuestionMovesRef.current;
		let savedMoves = 0;

		setLoaderDisplay(true);
		const saveMove = (move) => (move.kind === SECTION_MOVE_KIND
			? FormService.reorderFormSection(formId, move.sectionId, { sectionId: move.parentId, prevSectionId: move.prevSectionId })
			: FormService.reorderFormField(formId, move.fieldId, { sectionId: move.sectionId, prevFieldId: move.prevFieldId }));
		moves.reduce((chain, move) => chain.then(() =>
			saveMove(move)
				.then(() => { savedMoves += 1; })
		), Promise.resolve())
			.then(() => FormService.publishForms(formId))
			.then(res => {
				updatePendingQuestionMoves([]);
				if (res.status !== 200) return;

				toast.success(t('Success.label'), { position: toast.POSITION.TOP_RIGHT });
				setSectionPublishState(SECTION_PUBLISH_STATE.PUBLISHED);
				return FormService.getFormPreview(formId, true).then(preview => {
					if (preview.status === 200) setFormSkeleton(preview.data);
				});
			})
			// Keep only the moves that were not saved, so Publish can retry them
			.catch(err => updatePendingQuestionMoves(moves.slice(savedMoves)))
			.finally(() => setLoaderDisplay(false));
	};

	// The "Published" badge is a temporary confirmation: hide it after a while.
	// A new section change sets PENDING first, which clears this timer through the effect cleanup.
	useEffect(() => {
		if (sectionPublishState !== SECTION_PUBLISH_STATE.PUBLISHED) return undefined;

		const timer = setTimeout(() => {
			setSectionPublishState(state => (state === SECTION_PUBLISH_STATE.PUBLISHED ? null : state));
		}, PUBLISHED_STATUS_VISIBLE_MS);
		return () => clearTimeout(timer);
	}, [sectionPublishState]);

	// ---- Publish validation: every section/sub-section needs at least one question somewhere inside it ----
	const sectionHasQuestions = (section) =>
		(section?.fields?.length ?? 0) > 0 || (section?.subSections ?? []).some(sectionHasQuestions);

	// Returns the topmost empty sections (an empty parent is reported once, not each empty child),
	// each with the ids of its ancestors so the tree can be expanded down to it
	const collectEmptySections = (sections, ancestorIds = [], result = []) => {
		(sections ?? []).forEach(section => {
			if (!sectionHasQuestions(section)) {
				result.push({ section, ancestorIds });
				return;
			}
			collectEmptySections(section?.subSections, [...ancestorIds, section.id], result);
		});
		return result;
	};

	const onPublishClick = () => {
		const emptySections = collectEmptySections(formSkeleton?.sections);
		if (emptySections.length === 0) {
			setShowPublishConfirm(true);
			return;
		}

		toast.error(
			t('AddQuestionToSectionsBeforePublishing.label', {
				sections: emptySections.map(({ section }) => section.title).join(', ')
			}),
			{ position: toast.POSITION.TOP_RIGHT }
		);
		// Point the user at the first empty section: expand its parents, clear a search that could hide it, select and focus it
		const { section: firstEmpty, ancestorIds } = emptySections[0];
		setSectionSearch('');
		setSectionVisibility(visibility => ancestorIds.reduce((next, id) => ({ ...next, [id]: true }), { ...visibility }));
		setSelectedSection({ id: firstEmpty.id, title: firstEmpty.title });
		setFocusSectionId(firstEmpty.id);
	};

	const renderSectionPublishStatus = () => {
		if (sectionPublishState === SECTION_PUBLISH_STATE.PENDING) {
			return (
				<div className="dce-actionbar__publish" role="status">
					<span className="dce-actionbar__publish-text">
						<span className="dce-actionbar__publish-dot" aria-hidden />
						{t('UnpublishedSectionChanges.label')}
					</span>
					<button type="button" className="dce-actionbar__publish-btn" onClick={onPublishClick}>
						<FiUploadCloud aria-hidden /> {t('Publish.label')}
					</button>
				</div>
			);
		}
		if (sectionPublishState === SECTION_PUBLISH_STATE.PUBLISHED) {
			return (
				<span className="dce-actionbar__published" role="status">
					<FaCheck aria-hidden /> {t('Published.label')}
				</span>
			);
		}
		return null;
	};

	const onFormSectionCreated = (section, parentId) => {
		closeSectionCreateModal();
		if (section?.id != null) setCreatedSectionIds(ids => [...ids, section.id]);
		// New sections are saved but not published; show Publish until the user publishes them
		setSectionPublishState(SECTION_PUBLISH_STATE.PENDING);
		const formId = dataToSave.assessmentForm?.id;
		// The API adds a section at the end of its list; move it to the first position under the same parent
		// (0 = top level, otherwise the sub-section's own parent, so the hierarchy is unchanged).
		// If that fails the section still shows up, just at the end
		const moveToTop = section?.id != null
			? FormService.reorderFormSection(formId, section.id, { sectionId: parentId ?? 0, prevSectionId: 0 }).catch(err => { })
			: Promise.resolve();
		// Returned so FormSectionModal keeps the global loader on until the (slow) form reload finishes
		return moveToTop.then(() => FormService.getFormPreview(formId, true)).then(res => {
			if (res.status !== 200) return;

			const skeleton = applyFetchedSkeleton(res.data);
			if (parentId) setSectionVisibility(visibility => ({ ...visibility, [parentId]: true }));
			const created = findSkeletonSection(skeleton?.sections, section?.id);
			if (!created) return;

			// Select the new item and focus it once the tree has re-rendered; clear any search that could hide it
			setSectionSearch('');
			setSelectedSection({ id: created.id, title: created.title });
			setFocusSectionId(created.id);
		}).catch(err => { });
	};

	// Section/sub-section whose tree node should be scrolled to and focused after the next render
	const [focusSectionId, setFocusSectionId] = useState(null);

	useEffect(() => {
		if (focusSectionId == null) return;

		const nodeEl = document.querySelector(`.perm-node__main[data-section-id="${focusSectionId}"]`);
		if (!nodeEl) return;

		nodeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
		nodeEl.focus({ preventScroll: true });
		setFocusSectionId(null);
	}, [focusSectionId, formSkeleton, sectionVisibility]);

	const canEditSections = dataToSave.assessmentForm?.id > 0;

	const [sectionDelete, setSectionDelete] = useState({ show: false, section: null, isSubsection: false });

	const openSectionDeleteModal = (section, isSubsection) => {
		setSectionDelete({ show: true, section: section, isSubsection: isSubsection });
	};

	const closeSectionDeleteModal = () => setSectionDelete({ ...sectionDelete, show: false });

	const collectSectionIds = (sections, ids = new Set()) => {
		sections?.forEach(section => {
			ids.add(section.id);
			collectSectionIds(section?.subSections, ids);
		});
		return ids;
	};

	// Reload the skeleton and drop permissions/selection pointing at sections that no longer exist
	const onFormSectionDeleted = (deletedSectionId) => {
		closeSectionDeleteModal();
		setCreatedSectionIds(ids => ids.filter(id => id !== deletedSectionId));
		setSectionPublishState(SECTION_PUBLISH_STATE.PENDING);
		const formId = dataToSave.assessmentForm?.id;
		// Returned so DeleteFormSectionModal keeps the global loader on until the form reload finishes
		return FormService.getFormPreview(formId, true).then(res => {
			if (res.status !== 200) return;

			const sectionIds = collectSectionIds(res.data?.sections);
			const exists = (id) => id === -1 || sectionIds.has(id);
			applyFetchedSkeleton(res.data);
			setDataToSave(data => ({
				...data,
				formPermissions: (data.formPermissions ?? []).filter(p => exists(p.section?.id))
			}));
			setSelectedSection(selected => (selected && !exists(selected.id) ? null : selected));
		}).catch(err => { });
	};

	// ---- Question drag and drop (native HTML5 DnD) ----
	// dropTarget = { sectionId, fieldId, position } with position 'before' | 'after' (a question) or 'end' (a section card)
	const [draggedQuestionId, setDraggedQuestionId] = useState(null);
	const [questionDropTarget, setQuestionDropTarget] = useState(null);

	const updateQuestionDropTarget = (target) => {
		const current = questionDropTarget;
		if (current?.sectionId === target.sectionId && current?.fieldId === target.fieldId && current?.position === target.position) return;
		setQuestionDropTarget(target);
	};

	const onQuestionDragStart = (e, questionId) => {
		e.stopPropagation();
		e.dataTransfer.effectAllowed = 'move';
		// Firefox only starts a drag when some data is set
		e.dataTransfer.setData('text/plain', String(questionId));
		setDraggedQuestionId(questionId);
	};

	const onQuestionDragEnd = () => {
		setDraggedQuestionId(null);
		setQuestionDropTarget(null);
	};

	const onQuestionDragOver = (e, sectionId, questionId) => {
		if (draggedQuestionId == null) return;
		e.preventDefault();
		e.stopPropagation();
		e.dataTransfer.dropEffect = 'move';
		const rect = e.currentTarget.getBoundingClientRect();
		const position = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
		updateQuestionDropTarget({ sectionId: sectionId, fieldId: questionId, position: position });
	};

	const onSectionDragOver = (e, sectionId) => {
		if (draggedQuestionId == null) return;
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		updateQuestionDropTarget({ sectionId: sectionId, fieldId: null, position: 'end' });
	};

	const onQuestionDrop = (e) => {
		if (draggedQuestionId == null) return;
		e.preventDefault();
		e.stopPropagation();

		const target = questionDropTarget;
		const move = target && buildQuestionMove(formSkeleton, draggedQuestionId, target.sectionId, target.fieldId, target.position);
		onQuestionDragEnd();
		commitQuestionMove(move);
	};

	// Shared by drag and drop and the Move question dialog: show the move now, save it on Publish
	const commitQuestionMove = (move) => {
		if (!move) return false;

		const nextSkeleton = applyQuestionMove(formSkeleton, move);
		if (!nextSkeleton) return false;

		setFormSkeleton(nextSkeleton);
		updatePendingQuestionMoves([...pendingQuestionMovesRef.current, move]);
		setSectionPublishState(SECTION_PUBLISH_STATE.PENDING);
		setSectionVisibility(visibility => ({ ...visibility, [move.sectionId]: true }));
		return true;
	};

	// ---- Move question dialog ----
	const [questionToMove, setQuestionToMove] = useState(null);

	const openMoveQuestionModal = (e, question, fromSectionId) => {
		e.stopPropagation();
		setQuestionToMove({ question: question, fromSectionId: fromSectionId });
	};

	const closeMoveQuestionModal = () => setQuestionToMove(null);

	// Lands at the top of the destination section, like the form builder's Move question
	const onMoveQuestionToSection = (targetSectionId) => {
		const moved = commitQuestionMove({ fieldId: questionToMove?.question?.id, sectionId: targetSectionId, prevFieldId: 0 });
		closeMoveQuestionModal();
		if (moved) toast.success(t('QuestionMovedSuccessfully.label'), { position: toast.POSITION.TOP_RIGHT });
	};

	// ---- Sub-section drag and drop (native HTML5 DnD) ----
	// dropTarget = { sectionId, position } with position 'before' | 'after' (a sub-section) or 'inside' (a section,
	// appended as its last sub-section); top-level sections themselves stay in place
	const [draggedSectionId, setDraggedSectionId] = useState(null);
	const [sectionDropTarget, setSectionDropTarget] = useState(null);

	// Scroll the tree's container while a question or sub-section is dragged near its top/bottom edge
	const permTreeRef = useRef(null);
	useDragAutoScroll(draggedQuestionId != null || draggedSectionId != null, permTreeRef);

	const onSubSectionDragStart = (e, sectionId) => {
		e.stopPropagation();
		e.dataTransfer.effectAllowed = 'move';
		// Firefox only starts a drag when some data is set
		e.dataTransfer.setData('text/plain', String(sectionId));
		setDraggedSectionId(sectionId);
	};

	const onSubSectionDragEnd = () => {
		setDraggedSectionId(null);
		setSectionDropTarget(null);
	};

	const onSubSectionDragOver = (e, sectionId, depth) => {
		const rect = e.currentTarget.getBoundingClientRect();
		const position = depth === 0 ? 'inside' : (e.clientY < rect.top + rect.height / 2 ? 'before' : 'after');
		// No preventDefault for a drop that is not allowed or changes nothing, so the browser shows "no drop"
		if (!buildSectionMove(formSkeleton, draggedSectionId, sectionId, position)) {
			if (sectionDropTarget) setSectionDropTarget(null);
			return;
		}
		e.preventDefault();
		e.stopPropagation();
		e.dataTransfer.dropEffect = 'move';
		if (sectionDropTarget?.sectionId === sectionId && sectionDropTarget?.position === position) return;
		setSectionDropTarget({ sectionId: sectionId, position: position });
	};

	// Same as question moves: show the move now, save it on Publish
	const onSubSectionDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		const target = sectionDropTarget;
		const move = target && buildSectionMove(formSkeleton, draggedSectionId, target.sectionId, target.position);
		onSubSectionDragEnd();
		const nextSkeleton = move && applySectionMove(formSkeleton, move);
		if (!nextSkeleton) return;

		setFormSkeleton(nextSkeleton);
		updatePendingQuestionMoves([...pendingQuestionMovesRef.current, move]);
		setSectionPublishState(SECTION_PUBLISH_STATE.PENDING);
		setSectionVisibility(visibility => ({ ...visibility, [move.parentId]: true }));
	};

	const getSectionDropClass = (sectionId) => {
		if (sectionDropTarget?.sectionId !== sectionId) return '';
		if (sectionDropTarget.position === 'inside') return 'is-drop-target';
		return sectionDropTarget.position === 'before' ? 'is-section-drop-before' : 'is-section-drop-after';
	};

	// A section card takes either a dragged question or a dragged sub-section
	const getSectionDropProps = (sectionId, depth) => (canEditSections ? {
		onDragOver: (e) => (draggedSectionId != null ? onSubSectionDragOver(e, sectionId, depth) : onSectionDragOver(e, sectionId)),
		onDrop: (e) => (draggedSectionId != null ? onSubSectionDrop(e) : onQuestionDrop(e))
	} : {});

	const renderPermissionTable = () => {
		const hasExpandable = collectExpandableSectionIds(formSkeleton?.sections).length > 0;
		const canCreateSections = canEditSections;
		const isSearching = normalizedSectionSearch.length > 0;
		const sectionEntries = isSearching
			? filterSectionEntries(formSkeleton?.sections)
			: buildSectionEntries(formSkeleton?.sections);
		const showCompleteForm = !isSearching || sectionTitleMatches(t('CompleteForm.label'));
		const hasResults = showCompleteForm || sectionEntries.length > 0;
		return (
			<>
				<div className="card perm-tree">
					<div className="perm-tree__header">
						<span className="perm-tree__header-title">
							{t('SectionOrSubSection.label')}

						</span>
						{(canCreateSections || hasExpandable) && (
							<div className="perm-tree__header-actions">
								{canCreateSections && (
									<>
										<button type="button" onClick={() => openSectionCreateModal(false)}>
											<FiPlus aria-hidden /> {t('AddSection.label')}
										</button>
										<button type="button" disabled={!formSkeleton?.sections?.length}
											onClick={() => openSectionCreateModal(true)}>
											<FiPlus aria-hidden /> {t('AddSubSection.label')}
										</button>
									</>
								)}
								{hasExpandable && (
									<>
										<button type="button" onClick={() => setAllSectionsExpanded(true)}>
											{t('ExpandAll.label')}
										</button>
										<button type="button" onClick={() => setAllSectionsExpanded(false)}>
											{t('CollapseAll.label')}
										</button>
									</>
								)}
							</div>
						)}
						<div className="perm-tree__search">
							<FiSearch className="perm-tree__search-icon" aria-hidden />
							<input type="search" className="form-control form-control-sm"
								value={sectionSearch}
								placeholder={t('SearchSectionsSubSectionsOrQuestions.label')}
								aria-label={t('SearchSectionsSubSectionsOrQuestions.label')}
								onChange={(e) => onSectionSearchChange(e.target.value)} />
						</div>
					</div>
					{hasResults ? (
						<ul className="perm-tree__list" role="tree" aria-label={t('SectionOrSubSection.label')} ref={permTreeRef}>
							{showCompleteForm && renderPermissionNode({
								id: -1,
								title: t('CompleteForm.label'),
								badge: { type: 'form', label: t('Form.label') },
								questionCount: formSkeleton ? countQuestionsInSkeleton(formSkeleton) : 0
							})}
							{sectionEntries.map(entry => renderSectionNode(entry))}
						</ul>
					) : (
						<div className="perm-tree__empty">
							<FiSearch className="perm-tree__empty-icon" aria-hidden />
							<span>{t('NoSectionsSubSectionsOrQuestionsMatch.label', { query: sectionSearch.trim() })}</span>
						</div>
					)}
				</div>
				{canCreateSections && (
					<FormSectionModal
						show={sectionCreate.show}
						formId={dataToSave.assessmentForm?.id}
						formType={formSkeleton?.formType ?? forms?.find(form => form.id === dataToSave.assessmentForm?.id)?.formType}
						isSubsection={sectionCreate.isSubsection}
						parentSections={formSkeleton?.sections ?? []}
						defaultParentId={sectionCreate.parentId}
						onHide={closeSectionCreateModal}
						onCreated={onFormSectionCreated} />
				)}
				{canEditSections && (
					<DeleteFormSectionModal
						show={sectionDelete.show}
						formId={dataToSave.assessmentForm?.id}
						section={sectionDelete.section}
						isSubsection={sectionDelete.isSubsection}
						onHide={closeSectionDeleteModal}
						onDeleted={onFormSectionDeleted} />
				)}
				{canEditSections && (
					<MoveQuestionModal
						show={!!questionToMove}
						question={questionToMove?.question}
						fromSectionId={questionToMove?.fromSectionId}
						sections={formSkeleton?.sections ?? []}
						onHide={closeMoveQuestionModal}
						onMove={onMoveQuestionToSection} />
				)}
				{showPublishConfirm && (
					<ConfirmationModal
						modalShow={showPublishConfirm}
						title={t('Publish.label')}
						messageText={t('PublishConfirmationMsg.label')}
						callbackModal={onPublishSectionChanges} />
				)}
			</>
		);
	};

	const renderSupplierSelect = () => (
		<Form validated={permsValidated}>
			<Multiselect
				placeholder={t('SelectSuppliers.label')}
				options={suppliers}
				selectedValues={getSelectedSuppliers()}
				onSelect={(selectedList, selectedItem) => {
					if (selectedItem.companyId === 'selectAll') {
						const allSupplierIds = suppliers.map(supplier => supplier.companyId).filter(id => id !== 'selectAll');
						setDataToSave({ ...dataToSave, supplierIds: allSupplierIds });
						setSuppliers(suppliers.filter(supplier => supplier.companyId !== 'selectAll'));
					} else {
						onSelectDeSelectSupplier(selectedItem);
					}
				}}
				onRemove={(selectedList, selectedItem) => {
					onSelectDeSelectSupplier(selectedItem);
				}}
				displayValue="legalName"
			/>
		</Form>
	);

	// Questions placed directly in this section/sub-section (nested sub-sections render their own rows)
	const getQuestionDropClass = (sectionId, questionId) => {
		const target = questionDropTarget;
		if (target?.sectionId !== sectionId || target?.fieldId !== questionId) return '';
		return target.position === 'before' ? 'is-drop-before' : 'is-drop-after';
	};

	const renderSectionQuestions = (section) => {
		// Keep each question's original number; while searching a section that only matched on
		// questions, list just the matching ones
		const showOnlyMatches = normalizedSectionSearch.length > 0 && !sectionTitleMatches(section?.title)
			&& sectionHasQuestionMatch(section);
		const questions = (section?.fields ?? [])
			.map((question, index) => ({ question, index }))
			.filter(({ question }) => !showOnlyMatches || sectionTitleMatches(question?.name));
		if (questions.length === 0) return null;

		return (
			<div className="section-questions">

				<ol className="section-questions__list">
					{questions.map(({ question, index }) => {
						const isDraggable = canEditSections && question.id != null;
						const dragClass = draggedQuestionId === question.id ? 'is-dragging' : '';
						return (
							<li key={`sectionQuestion-${section.id}-${question.id ?? index}`}
								className={`section-questions__item ${isDraggable ? 'is-draggable' : ''} ${dragClass} ${getQuestionDropClass(section.id, question.id)}`}
								draggable={isDraggable}
								onDragStart={isDraggable ? (e) => onQuestionDragStart(e, question.id) : undefined}
								onDragEnd={isDraggable ? onQuestionDragEnd : undefined}
								onDragOver={isDraggable ? (e) => onQuestionDragOver(e, section.id, question.id) : undefined}
								onDrop={isDraggable ? onQuestionDrop : undefined}>
								{isDraggable && <MdDragIndicator className="section-questions__grip" aria-hidden />}
								<span className="section-questions__number">Q{index + 1}</span>
								<span className="section-questions__text" title={question.name}>{renderHighlightedTitle(question.name)}</span>
								{isDraggable && (
									<button type="button" className="section-questions__move"
										aria-label={t('MoveQuestion.label')}
										title={t('MoveQuestion.label')}
										onClick={(e) => openMoveQuestionModal(e, question, section.id)}>
										<FiCornerDownRight aria-hidden />
									</button>
								)}
							</li>
						);
					})}
				</ol>
			</div>
		);
	};

	// ---- Page layout ----

	// Same page for create, update and duplicate; only the wording changes

	const pageSteps = [
		{
			card: CardType.FORM_SELECTION,
			title: t('SelectForm.label'),
			caption: t('FrequencyAndForm.label')
		},
		{
			card: CardType.FORM_DETAILS,
			title: t('Schedule.label'),
			caption: t('PeriodAndExpiry.label')
		},
		{
			card: selfAssessment ? CardType.PERMISSION_CONFIGURATION : CardType.PERMISSION,
			title: t('Permissions.label'),
			caption: t('AssignGroupsToSections.label')
		},
		{
			card: CardType.CONFIRMATION,
			title: t('Review.label'),
			caption: t(isUpdate ? 'ConfirmAndUpdate.label' : 'ConfirmAndCreate.label')
		}
	];
	const hasSelectedSection = selectedSection?.id > 0 || selectedSection?.id === -1;

	const onPageNext = () => {
		if (activeCard === CardType.FORM_SELECTION && !dataToSave.assessmentForm?.id) {
			toast.warning(t('SelectFormWarning.label'), { position: toast.POSITION.TOP_RIGHT });
			return;
		}
		onNext();
	};

	// Section/question changes alter the shared form, so step navigation (Back, Next, stepper) stays
	// disabled while the Publish button is showing, until the changes are published
	const hasUnpublishedSectionChanges = sectionPublishState === SECTION_PUBLISH_STATE.PENDING;
	const navBlockedTitle = hasUnpublishedSectionChanges ? t('PublishChangesBeforeContinuing.label') : undefined;

	const getPageScheduleRows = () => {
		const notSet = t('NotSet.label');
		const periodText = recurringConfig.active
			? `${moment(recurringConfig.startDate).format('MMM YYYY')} – ${moment(recurringConfig.endDate).format('MMM YYYY')}`
			: (dataToSave.period
				? Utils.reportingPeriodToReadableText(dataToSave.period, dataToSave.regulation?.frequency, t)
				: notSet);
		const rows = [
			{ label: t('Frequency.label'), value: t(`${dataToSave.frequency}.label`) },
			{ label: t('Occurs.label'), value: recurringConfig.active ? t('Recurring.label') : t('Once.label') },
			{ label: t('ReportingPeriod.label'), value: periodText }
		];
		if (recurringConfig.active) {
			rows.push({ label: t('ValidFor.label'), value: t('DaysCount.label', { count: recurringConfig.expiringDay }) });
		} else {
			rows.push({
				label: t('Expiry.label'),
				value: dataToSave.lastSubmissionDate ? moment(dataToSave.lastSubmissionDate).format('MMM DD, YYYY') : notSet
			});
		}
		return rows;
	};

	const renderPageSummaryList = (rows) => (
		<dl className="dce-summary-list">
			{rows.map(row => (
				<div className="dce-summary-list__row" key={row.label}>
					<dt>{row.label}</dt>
					<dd>{row.value}</dd>
				</div>
			))}
		</dl>
	);

	const getPageTitleKey = () => {
		if (isDuplicate) return 'DuplicateDataCollectionEvent.label';
		return isUpdate ? 'UpdateDataCollectionEvent.label' : 'CreateDataCollectionEvent.label';
	};

	const renderPageHeader = () => (
		<header className="dce-page-header">

			<div className="dce-page-header__text">
				<h5 className="dce-page-header__title">{t(getPageTitleKey())}</h5>
				<p className="dce-page-header__subtitle">
					{isDuplicate
						? t('DuplicatingAsNewDataCollectionEvent.label', { name: dataToSave.assessmentForm?.name ?? '' })
						: t(isUpdate ? 'UpdateScheduleAndPermissionsThenReview.label' : 'SetUpDataCollectionEventInFourSteps.label')}
				</p>
			</div>
		</header>
	);

	const renderPageStepper = () => (
		<ol className="dce-stepper">
			{pageSteps.map((step, index) => {
				const status = activeCard > step.card ? 'done' : (activeCard === step.card ? 'active' : 'upcoming');
				return (
					<li key={`dceStep-${step.card}`} className={`dce-stepper__item dce-stepper__item--${status}`}>
						<button type="button" className="dce-stepper__button"
							disabled={status !== 'done' || hasUnpublishedSectionChanges}
							aria-current={status === 'active' ? 'step' : undefined}
							onClick={() => setActiveCard(step.card)}>
							<span className="dce-stepper__marker">{status === 'done' ? <FaCheck /> : index + 1}</span>
							<span className="dce-stepper__text">
								<span className="dce-stepper__title">{step.title}</span>
								<span className="dce-stepper__caption">{step.caption}</span>
							</span>
						</button>
					</li>
				);
			})}
		</ol>
	);

	const renderPageStepHeading = (title, hint, aside) => (
		<div className="dce-step__heading">
			<div>
				<h6 className="dce-step__title">{title}</h6>
				{hint && <p className="dce-step__hint">{hint}</p>}
			</div>
			{aside}
		</div>
	);

	const renderPageFormCard = (form) => {
		const isSelected = form.id === dataToSave.assessmentForm?.id;
		return (
			<button type="button" key={`dceFormCard-${form.id}`}
				className={`dce-form-card ${isSelected ? 'is-selected' : ''}`}
				aria-pressed={isSelected}
				onClick={() => onFormSelection(form, form.regulation, false)}
				onDoubleClick={() => onFormSelection(form, form.regulation, true)}>
				<span className="dce-form-card__check"><FaCheck /></span>
				<div className="dce-form-card__head">
					<img className="dce-form-card__logo" alt=""
						src={form.logo?.id > 0 ? FormService.getLogo(form.logo?.id, form.id) : GRI_pic} />
					<div className="dce-form-card__heading">
						<span className="dce-form-card__name">{form.name}</span>
						<span className="dce-form-card__date">
							{t('UpdatedOn.label')} {moment(form?.lastModifiedDate).format('MMM DD, YYYY')}
						</span>
					</div>
				</div>
				{form.description && <p className="dce-form-card__description">{form.description}</p>}
				<div className="dce-form-card__stats">
					<span><strong>{form?.sectionCount ?? 0}</strong> {t('Sections.label')}</span>
					<span><strong>{form?.subSectionCount ?? 0}</strong> {t('SubSections.label')}</span>
					<span><strong>{form?.questionCount ?? 0}</strong> {t('Questions.label')}</span>
				</div>
			</button>
		);
	};

	const renderPageFormSelection = () => {
		const publishedForms = forms?.filter(form => form.status === 'PUBLISHED') ?? [];
		return (
			<section className="dce-step">
				{renderPageStepHeading(
					t('ChooseAForm.label'),
					t('FormSelectionCardText.label'),
					<div className="dce-segmented" role="radiogroup" aria-label={t('Frequency.label')}>
						{Object.values(FormFrequency).map(({ text }) => (
							<button type="button" key={`dceFreq-${text}`} role="radio"
								aria-checked={dataToSave.frequency === text}
								className={`dce-segmented__option ${dataToSave.frequency === text ? 'is-active' : ''}`}
								onClick={() => dataToSave.frequency !== text && onFrequencyChange(text)}>
								{t(`${text}.label`)}
							</button>
						))}
					</div>
				)}
				{publishedForms.length === 0 ? (
					<div className="dce-empty">{t('NoPublishedForms.label')}</div>
				) : (
					<>
						<div className="dce-form-grid">
							{publishedForms.map(form => renderPageFormCard(form))}
						</div>
						<div className="dce-step__pagination">
							<PaginationComponent
								itemsTotalCount={itemsTotalCount}
								itemsPerPage={itemsPerPage}
								currentPage={currentPage}
								setCurrentPage={setCurrentPage}
								alwaysShown={true}
								setItemsPerPage={setItemsPerPage}
								pageClicked={(pageNum, perPage) => getForms(pageNum, perPage, dataToSave.frequency)}
							/>
						</div>
					</>
				)}
			</section>
		);
	};

	const renderPageOccurs = () => {
		if (id > 0 && !periodEditable) return null;

		const options = [
			{ active: false, label: t('Once.label'), description: t('CollectDataForSingleReportingPeriod.label') },
			{ active: true, label: t('Recurring.label'), description: t('CreateEventsAutomaticallyOnSchedule.label') }
		];
		return (
			<div className="dce-occurs">
				<span className="dce-field-label">{t('Occurs.label')}</span>
				<div className="dce-occurs__options" role="radiogroup" aria-label={t('Occurs.label')}>
					{options.map(option => {
						const isActive = recurringConfig.active === option.active;
						return (
							<button type="button" key={`dceOccurs-${option.active}`} role="radio" aria-checked={isActive}
								className={`dce-occurs__option ${isActive ? 'is-active' : ''}`}
								onClick={() => !isActive && enableOrDisableCalendarEvent(option.active)}>
								<span className="dce-occurs__radio" />
								<span>
									<span className="dce-occurs__label">{option.label}</span>
									<span className="dce-occurs__description">{option.description}</span>
								</span>
							</button>
						);
					})}
				</div>
			</div>
		);
	};

	const renderPageSchedule = () => (
		<section className="dce-step dce-step--split">
			<div className="dce-panel">
				{renderPageStepHeading(t('WhenShouldDataBeCollected.label'), t('CollectOnceOrOnRecurringSchedule.label'))}
				<Form noValidate validated={detailsValidated} className="dce-schedule-form">
					{renderPageOccurs()}
					{renderRecurringConfig()}
					{!recurringConfig.active && renderOnceFields()}
				</Form>
			</div>
			<aside className="dce-panel dce-panel--muted">
				<h6 className="dce-panel__title">{t('EventSummary.label')}</h6>
				{renderPageSummaryList([
					{ label: t('Form.label'), value: dataToSave.assessmentForm?.name ?? t('NotSet.label') },
					...getPageScheduleRows()
				])}
			</aside>
		</section>
	);

	const renderPagePermissionAlert = () => (
		<div className="dce-alert dce-alert--danger" role="alert">
			<span className="dce-alert__icon"><FiAlertCircle aria-hidden /></span>
			<div className="dce-alert__content">
				<span className="dce-alert__title">{t('PermissionRequired.label')}</span>
				<span className="dce-alert__message">{t('AddOnePermission.label')}</span>
			</div>
			<button type="button" className="dce-alert__close"
				aria-label={t('Close.label')} title={t('Close.label')}
				onClick={() => setPermsValidated(false)}>
				<FiX aria-hidden />
			</button>
		</div>
	);

	const renderPagePermissions = () => (
		<section className="dce-step">
			{renderPageStepHeading(
				t('PermissionConfigHeaders.label'),
				selfAssessment ? t('PermissionConfigText.label') : null,
				selfAssessment && (
					<span className="dce-badge">
						{t('SectionsAssignedCount.label', { count: dataToSave.formPermissions?.length ?? 0 })}
					</span>
				)
			)}
			{permsValidated && renderPagePermissionAlert()}
			{selfAssessment ? (
				<div className="assessment_modal_card dce-permissions">
					<div className="assessmentCard_form dce-permissions__grid">
						<div className=" dce-permissions__sections">
							{renderPermissionTable()}
						</div>
						<div className="dce-permissions__groups">
							{hasSelectedSection ? (
								<>
									<div className="dce-permissions__groups-head">
										{t('AssignGroupsTo.label')} <strong>{selectedSection.title}</strong>
									</div>
									{renderPermissionGroups()}
								</>
							) : (
								<div className="dce-empty">{t('SelectSectionToAssignGroups.label')}</div>
							)}
						</div>
					</div>
				</div>
			) : (
				<div className="dce-panel">{renderSupplierSelect()}</div>
			)}
		</section>
	);

	// "Edit" shortcut back to the step that owns the reviewed data
	const renderReviewEditButton = (card) => (
		<button type="button" className="dce-review__edit" onClick={() => setActiveCard(card)}>
			<FiEdit2 aria-hidden /> {t('Edit.label')}
		</button>
	);

	// Form identity on top, schedule as a single row of facts underneath — one card, two sections
	const renderReviewSummary = () => {
		const form = dataToSave.assessmentForm;
		const stats = form?.questionCount != null ? [
			{ label: t('Sections.label'), value: form.sectionCount ?? 0 },
			{ label: t('SubSections.label'), value: form.subSectionCount ?? 0 },
			{ label: t('Questions.label'), value: form.questionCount ?? 0 }
		] : [];
		return (
			<div className="dce-panel dce-review">
				<div className="dce-review__hero">
					<div className="dce-review__form-text">
						<span className="dce-review__eyebrow">{t('Form.label')}</span>
						<div className="dce-review__form-name">{form?.name}</div>
						{form?.description && <p className="dce-review__form-description">{form.description}</p>}
						{stats.length > 0 && (
							<div className="dce-review__stats">
								{stats.map(stat => (
									<span key={stat.label}><strong>{stat.value}</strong> {stat.label}</span>
								))}
							</div>
						)}
					</div>
					{renderReviewEditButton(CardType.FORM_SELECTION)}
				</div>
				<div className="dce-review__section">
					<div className="dce-review__section-head">
						<span className="dce-review__eyebrow">{t('Schedule.label')}</span>
						{renderReviewEditButton(CardType.FORM_DETAILS)}
					</div>
					<dl className="dce-review__details">
						{getPageScheduleRows().map(row => (
							<div className="dce-review__detail" key={row.label}>
								<dt>{row.label}</dt>
								<dd>{row.value}</dd>
							</div>
						))}
					</dl>
				</div>
			</div>
		);
	};

	const renderReviewPermissionRows = () => (
		<>
			<div className="dce-review__perm-row dce-review__perm-row--head">
				<span>{t('SectionOrSubSection.label')}</span>
				<span>{t('Groups.label')}</span>
			</div>
			{dataToSave.formPermissions.map(perm => (
				<div className="dce-review__perm-row" key={`dceReviewPerm-${perm.section?.id}`}>
					<span className="dce-review__perm-section">{perm.section?.title}</span>
					<div className="dce-review__perm-groups">
						{perm?.groups?.map(group => (
							<span className="dce-review__tag" key={`dceReviewGroup-${perm.section?.id}-${group.id}`}>
								{groups?.find(g => g.id === group.id)?.groupName ?? ''}
							</span>
						))}
					</div>
				</div>
			))}
		</>
	);

	const renderReviewSuppliers = () => (
		<div className="dce-review__suppliers">
			{dataToSave.supplierIds.map(supplierId => {
				const name = suppliers?.find(sup => sup.companyId === supplierId)?.legalName ?? '';
				return (
					<span className="dce-review__supplier" key={`dceReviewSupplier-${supplierId}`}>
						<span className="dce-review__supplier-avatar" aria-hidden>{name.charAt(0).toUpperCase()}</span>
						<span className="dce-review__supplier-name">{name}</span>
					</span>
				);
			})}
		</div>
	);

	const renderReviewPermissions = () => {
		const count = selfAssessment ? (dataToSave?.formPermissions?.length ?? 0) : (dataToSave?.supplierIds?.length ?? 0);
		return (
			<div className="dce-panel dce-review__panel">
				<div className="dce-review__panel-head">
					<div className="dce-review__panel-heading">
						<span className="dce-review__eyebrow">{t('Permissions.label')}</span>
						<span className="dce-review__count">
							{selfAssessment
								? t('SectionsAssignedCount.label', { count })
								: `${count} ${t('Suppliers.label')}`}
						</span>
					</div>
					{renderReviewEditButton(pageSteps[2].card)}
				</div>
				{count === 0
					? <div className="dce-review__empty">{t('NoData.label')}</div>
					: (selfAssessment ? renderReviewPermissionRows() : renderReviewSuppliers())}
			</div>
		);
	};

	const renderPageReview = () => (
		<section className="dce-step">
			{renderPageStepHeading(t('Review.label'), t(isUpdate ? 'CheckDetailsBeforeUpdatingDataCollectionEvent.label' : 'CheckDetailsBeforeCreatingDataCollectionEvent.label'))}
			{renderReviewSummary()}
			{renderReviewPermissions()}
		</section>
	);

	const renderPageActionBar = () => (
		<div className="dce-actionbar">
			<div className="dce-actionbar__left">
				<Button variant="link" className="dce-actionbar__cancel" onClick={() => exitPage()}>
					{t('Cancel.label')}
				</Button>
			</div>
			<div className="dce-actionbar__right">
				{renderSectionPublishStatus()}
				{/* Wrapper spans carry the hint: disabled buttons get pointer-events: none, so their own title never shows */}
				{activeCard !== CardType.FORM_SELECTION && (
					<span className="dce-actionbar__nav d-inline-flex" title={navBlockedTitle}>
						<Button variant="outline-secondary" className="dce-actionbar__btn"
							disabled={hasUnpublishedSectionChanges} onClick={() => onBack()}>
							<FiChevronLeft aria-hidden /> {t('Back.label')}
						</Button>
					</span>
				)}
				{activeCard === CardType.CONFIRMATION ? (
					<Button variant="primary" className="dce-actionbar__btn dce-actionbar__btn--primary" onClick={() => saveAssessment()}>
						<FaCheck aria-hidden /> {t(isUpdate ? 'Update.label' : 'CreateEvent.label')}
					</Button>
				) : (
					<span className="dce-actionbar__nav d-inline-flex" title={navBlockedTitle}>
						<Button variant="primary" className="dce-actionbar__btn dce-actionbar__btn--primary"
							disabled={hasUnpublishedSectionChanges} onClick={() => onPageNext()}>
							{t('Next.label')} <FiChevronRight aria-hidden />
						</Button>
					</span>
				)}
			</div>
		</div>
	);

	const renderPageStep = () => {
		switch (activeCard) {
			case CardType.FORM_SELECTION: return renderPageFormSelection();
			case CardType.FORM_DETAILS: return renderPageSchedule();
			case CardType.PERMISSION:
			case CardType.PERMISSION_CONFIGURATION: return renderPagePermissions();
			case CardType.CONFIRMATION: return renderPageReview();
			default: return null;
		}
	};

	return (
		<div className="dce-create-page">
			{renderPageHeader()}
			{renderPageStepper()}
			<div className="dce-create-page__content">
				{renderPageStep()}
			</div>
			{renderPageActionBar()}
		</div>
	);
}

export default withTranslation()(DataCollectionEventForm);
